import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@mui/material'
import { Reveal, Etiqueta } from '../../brand'
import { trackCTAClick } from '../../utils/analytics'

type Tier = { name: string; detail: string; scope: string }
type Plan = { name: string; levels: string; summary: string }

/**
 * Quatro planos, um laço — envelope aninhado (modelo do Ângelo): Fábrica ⊃ Operação ⊃ Portfólio ⊃ Núcleo. Passar o mouse ou tocar
 * abre o nível de autonomia que o plano libera (resumo de brand.plans). Sem contagem de "projetos": número sem fonte não entra (D27).
 */
export function PlansStrip() {
  const { t } = useTranslation()
  const tiers = t('model.plansStrip.tiers', { returnObjects: true }) as Tier[]
  const plans = t('brand.plans.items', { returnObjects: true }) as Plan[]
  const [active, setActive] = useState<string | null>(null)
  const summary = (name: string) => plans.find((p) => p.name === name)?.summary ?? ''
  const btn = (tier: Tier) => {
    const on = active === tier.name
    return (
      <button type="button" className="z-nest__btn" aria-expanded={on} onClick={() => setActive(on ? null : tier.name)} onMouseEnter={() => setActive(tier.name)} onMouseLeave={() => setActive((c) => (c === tier.name ? null : c))}>
        <span className="z-nest__topo"><span className="z-nest__barra" aria-hidden="true" /><span className="z-nest__nome">{t('brand.plans.planWord')} {tier.name}</span><Etiqueta as="span">{tier.scope}</Etiqueta></span>
        <p className="z-nest__detalhe">{tier.detail}</p>
        <p className="z-nest__escopo"><span>{summary(tier.name)}</span></p>
      </button>
    )
  }
  // Fábrica (mais inclusivo) envolve Operação, Portfólio e Núcleo (o cartão interno) — um molde de costura, não uma grade
  const styles = ['z-nest z-nest--forte', 'z-nest', 'z-nest z-nest--tracejado']
  let node = <div className={['z-nest z-nest--tracejado', active === tiers[3].name && 'is-active'].filter(Boolean).join(' ')}>{btn(tiers[3])}</div>
  for (let i = 2; i >= 0; i -= 1) node = <div className={[styles[i], active === tiers[i].name && 'is-active'].filter(Boolean).join(' ')}>{btn(tiers[i])}{node}</div>
  return <Reveal className="z-nest-wrap">{node}</Reveal>
}

type Card = { name: string; forWhom: string; points: string[] }

/** Cartões dos quatro planos; Fábrica em destaque (o único com as três peças). CTA = conversa, sem preço (D20). */
export function PlansCards() {
  const { t } = useTranslation()
  const cards = t('model.plans.cards', { returnObjects: true }) as Card[]
  return (
    <Reveal as="div" stagger={0.1} y={16} className="z-plans">
      {cards.map((p, i) => {
        const featured = i === cards.length - 1
        return (
          <div key={p.name} className={['z-plan', featured && 'z-plan--destaque'].filter(Boolean).join(' ')} data-anatomy="campo">
            <div>
              {featured && <span className="z-plan__badge">{t('model.plans.featured')}</span>}
              <p className="z-plan__nome">{p.name}</p>
              <p className="z-plan__para">{p.forWhom}</p>
              <ul className="z-plan__lista">{p.points.map((pt) => <li key={pt}>{pt}</li>)}</ul>
            </div>
            <Button component={Link} to="/contacts" variant={featured ? 'contained' : 'outlined'} fullWidth onClick={() => trackCTAClick(`plans.card.${i}`, 'plans-cards', 'Plans', p.name)}>{t('model.plans.talk', { name: p.name })}</Button>
          </div>
        )
      })}
    </Reveal>
  )
}

type Row = { label: string; values: [string, string, string, string] }

/** Comparação linha a linha; a coluna Fábrica recebe o realce de Construção (é a única que também constrói). */
export function PlansComparison() {
  const { t } = useTranslation()
  const rows = t('model.plans.compare.rows', { returnObjects: true }) as Row[]
  const plans = (t('brand.plans.items', { returnObjects: true }) as Plan[]).map((p) => p.name)
  const included = t('model.plans.compare.included'), none = t('model.plans.compare.none')
  return (
    <Reveal y={16} className="z-compare">
      <table>
        <thead><tr><th scope="col" /> {plans.map((p, i) => <th key={p} scope="col" className={i === 3 ? 'is-destaque' : undefined}>{p}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}><th scope="row">{r.label}</th>{r.values.map((v, i) => <td key={i} className={[i === 3 && 'is-destaque', v === none && 'is-vazio', v === included && 'is-incluido'].filter(Boolean).join(' ') || undefined}>{v}</td>)}</tr>
          ))}
        </tbody>
      </table>
      <p className="z-compare__dica">{t('model.plans.compare.hint')}</p>
    </Reveal>
  )
}

/** Perguntas de quem compra — acordeão com a primeira aberta. */
export function PlansFAQ() {
  const { t } = useTranslation()
  const items = t('model.plans.faq.items', { returnObjects: true }) as Array<{ q: string; a: string }>
  const [open, setOpen] = useState<number | null>(0)
  return (
    <Reveal stagger={0.06} y={10} className="z-faq">
      {items.map((it, i) => {
        const on = open === i
        return (
          <div key={it.q} className="z-faq__item">
            <button type="button" className="z-faq__q" aria-expanded={on} aria-controls={`faq-${i}`} onClick={() => setOpen(on ? null : i)}>{it.q}<span className="z-faq__mais" aria-hidden="true">+</span></button>
            <div id={`faq-${i}`} className={['z-faq__a', on && 'is-open'].filter(Boolean).join(' ')}><p>{it.a}</p></div>
          </div>
        )
      })}
    </Reveal>
  )
}
